import React, { useEffect, useState, Fragment } from "react";
import {
  useTable,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
} from "react-table";
import Select from "react-select";
import { ReactComponent as ArrowRight } from "../../../../assets/icons/ArrowRight.svg";
import { DefaultColumnFilter } from "./filters";
import { ReactComponent as SearchIcon } from "../../../../assets/icons/Search.svg";
import { ReactComponent as ClearCrossIcon } from "../../../../assets/icons/ClearCross.svg";
import { ReactComponent as SortArrowIcon } from "../../../../assets/icons/SortArrow.svg";
import { dateFilters } from "../../../../constants/constants";
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  const [value, setValue] = useState(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <div
      className="d-flex"
      style={{
        gap: "10px",
        width: "fit-content",
        borderBottom: "1px solid lightgray",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <SearchIcon />
      <div
        className="d-flex"
        style={{ gap: "5px", justifyContent: "center", alignItems: "center" }}
      >
        <input
          value={value || ""}
          onChange={(e) => {
            setValue(e.target.value);
            onChange(e.target.value);
          }}
          placeholder={`Search by Name / Phone Number `}
          style={{
            border: "none",

            borderRadius: "0px",
            backgroundColor: "transparent",
            width: "15rem",
          }}
        />
        <ClearCrossIcon
          style={{ cursor: "pointer" }}
          onClick={() => {
            setValue("");
            setGlobalFilter("");
          }}
        />
      </div>
    </div>
  );
};

const DateFilter = ({ handleSelect }) => {
  return (
    <div className="date-filter-container">
      <span>Filter by date</span>
      <Select
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            padding: "0px",
            backgroundColor: "transparent",
            width: "10rem",
            border: "0px",
            outline: "0px",
            color: "#94A3B8 !important",
          }),
        }}
        components={{
          IndicatorSeparator: () => null,
        }}
        placeholder="Select"
        classNamePrefix="select"
        name="date-filter"
        options={dateFilters}
        defaultValue={dateFilters[0]}
        onChange={(e) => {
          handleSelect(e);
        }}
      />
    </div>
  );
};

const TableContainer = ({ columns, data, renderRowSubComponent }) => {
  const [pageList, setPageList] = useState([]);
  const [filter, setFilter] = useState(dateFilters[0]);

  const [filteredData, setFilteredData] = useState(data);

  function filterData() {
    switch (filter.value) {
      case "week":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const startOfWeek = new Date();
          startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
          return date >= startOfWeek;
        });
      case "pastweek":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const pastWeek = new Date();
          pastWeek.setDate(pastWeek.getDate() - 7);
          return date >= pastWeek;
        });
      case "2week":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const twoWeeksAgo = new Date();
          twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
          return date >= twoWeeksAgo;
        });
      case "month":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          return date >= startOfMonth;
        });
      case "3months":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          return date >= threeMonthsAgo;
        });
      case "6months":
        return data.filter((item) => {
          const date = new Date(item?.last_active);
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          return date >= sixMonthsAgo;
        });
      default:
        return data;
    }
  }

  useEffect(() => {
    setFilteredData(filterData());
  }, [filter]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    visibleColumns,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    preGlobalFilteredRows,
    rows,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = useTable(
    {
      columns,
      data: filteredData,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        sortBy: [
          {
            id: "last_active",
            aesc: true,
          },
        ],
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = (column) => {
    return column.isSorted ? (
      column.isSortedDesc ? (
        <SortArrowIcon
          style={{ transform: "rotate(180deg)", marginLeft: "5px" }}
        />
      ) : (
        <SortArrowIcon style={{ marginLeft: "5px" }} />
      )
    ) : (
      ""
    );
  };

  useEffect(() => {
    let tempPages = [];
    for (let i = pageIndex; i < pageCount; i++) {
      tempPages.push(i + 1);
    }
    setPageList(tempPages);
  }, []);

  return (
    <Fragment>
      <div className="d-flex my-3 justify-content-between container-fluid w-100">
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
        />
        <div className="d-flex" style={{ gap: "10px" }}>
          <DateFilter handleSelect={setFilter} />
        </div>
      </div>
      <table className="table my-3 px-2" {...getTableProps()}>
        <thead
          className="thead-light p-2"
          style={{ backgroundColor: "#F9FAFB" }}
        >
          {headerGroups.map((headerGroup) => (
            <tr
              {...headerGroup.getHeaderGroupProps()}
              className="py-3"
              style={{
                height: "4rem",
                verticalAlign: "middle",
              }}
            >
              {headerGroup.headers.map((column) => (
                <th
                  scope="col"
                  style={{ padding: "10px 2rem" }}
                  {...column.getHeaderProps()}
                >
                  <div {...column.getSortByToggleProps()}>
                    <strong className="muted-text">
                      {column.render("Header")}
                    </strong>
                    {generateSortingIndicator(column)}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody className="bg-white" {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <Fragment key={row.getRowProps().key}>
                <tr>
                  {row.cells.map((cell) => {
                    return (
                      <td
                        className="py-3"
                        style={{
                          height: "4rem",
                          padding: "10px 2rem",
                          verticalAlign: "middle",
                          textAlign: "left",
                          alignItems: "start",
                        }}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded && (
                  <tr
                    className="py-3"
                    style={{
                      height: "4rem",
                      verticalAlign: "middle",
                    }}
                  >
                    <td colSpan={visibleColumns.length}>
                      {renderRowSubComponent(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="d-flex justify-content-between w-100 container-fluid">
        <div>
          Showing {rows.length ? pageIndex * 10 + 1 : 0} to{" "}
          {(pageIndex + 1) * 10 < rows.length
            ? (pageIndex + 1) * 10
            : rows.length}{" "}
          of {rows.length} entries
        </div>
        <div className="d-flex" style={{ gap: "15px" }}>
          <button
            className="pagination-btn"
            onClick={previousPage}
            disabled={!canPreviousPage}
          >
            <ArrowRight
              style={{ transform: "rotate(180deg)", marginTop: "5%" }}
            />{" "}
            Prev
          </button>
          <div style={{ display: "flex", gap: "15px" }}>
            {pageList?.length > 3
              ? pageList
                  .slice(
                    pageIndex,
                    pageIndex + 3 > pageList.length
                      ? pageList.length
                      : pageIndex + 3
                  )
                  .map((number) => {
                    return (
                      <strong
                        onClick={(e) => {
                          gotoPage(number - 1);
                        }}
                        style={{ cursor: "pointer" }}
                        className={`number-pagination${
                          pageIndex + 1 === number && "-selected"
                        }`}
                        key={number - 1}
                      >
                        {number}
                      </strong>
                    );
                  })
              : pageList.map((number) => {
                  return (
                    <strong
                      onClick={(e) => {
                        gotoPage(number - 1);
                      }}
                      style={{ cursor: "pointer" }}
                      className={`number-pagination${
                        pageIndex + 1 === number && "-selected"
                      }`}
                      key={number - 1}
                    >
                      {number}
                    </strong>
                  );
                })}
            {/* {pageList.length > 3 && `${" "}...`} */}
          </div>
          <button
            className="pagination-btn"
            onClick={nextPage}
            disabled={!canNextPage}
          >
            Next <ArrowRight style={{ marginTop: "5%" }} />
          </button>
        </div>
      </div>
      <div style={{ height: "7vh" }} />
    </Fragment>
  );
};

export default TableContainer;
